import React, { Component } from "react";
import { withCookies, Cookies } from "react-cookie";
import { instanceOf } from "prop-types";
import { NotificationManager } from "react-notifications";

// Services
import providersService from "@services/providers";
import ordersService from "@services/orders";

// Components
import Menu from "@components/Menu";
import AutoCompleteField from "@components/AutoCompleteV2";
import TextField from "@components/TextField";
import Input from "@components/Input";
import Switch from "@components/Switch";
import {
  MdTurnLeft,
  MdAddCircleOutline
} from "react-icons/md";
import ClipLoader from "react-spinners/ClipLoader";

import UserContext from "@components/context/userContext";
import { currentDateTime, currentDate, dateToTimestamp } from '@utils/date';

import '../sales/form.css';
import './providers-form.css';

class ProvidersForm extends Component {
  static contextType = UserContext;

  static propTypes = {
    cookies: instanceOf(Cookies).isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      loaded: false,
      provider: null,
      providers: [],
      pendingOrders: [],
      selectedOrders: [],
      date: currentDateTime(),
      forma_pagamento: 1,
      numero_cheque: "",
      banco: "",
      saldo: 0,
      valor_desc_recibo: 0,
      obs: "",
      data_pre_datado: currentDate(),
      isSubmitting: false,
      total_valor_iliquido: 0,
      total_valor_iva: 0,
      total_valor_liquido: 0,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleFormCancel = this.handleFormCancel.bind(this);
    this.loadFunction = this.loadFunction.bind(this);
  }

  async loadFunction() {
    const profile = this.context;

    if (!this.state.loaded) {
      try {
        const providers = await providersService.get(profile.idEmpresa);
        
        this.setState({
          providers: providers || [],
          loaded: true,
        });
      } catch (error) {
        console.error('Erro ao carregar fornecedores:', error);
        NotificationManager.error("Erro ao carregar fornecedores", "Erro", 3000);
      }
    }
  }

  async loadPendingOrders(providerId) {
    const profile = this.context;

    try {
      const orders = await ordersService.getPendingOrders(profile.idEmpresa, {
        idprovider: providerId,
        status: 'all'
      });

      this.setState({
        pendingOrders: orders || [],
        selectedOrders: [],
        total_valor_iliquido: 0,
        total_valor_iva: 0,
        total_valor_liquido: 0,
      });
    } catch (error) {
      console.error('Erro ao carregar orders pendentes:', error);
      NotificationManager.error("Erro ao carregar orders pendentes", "Erro", 3000);
    }
  }

  handleChange(e, callback) {
    let value = e.target.value;
    let name = e.target.name;

    this.setState({
      [name]: value,
    });
  }

  toggleOrder(order) {
    const { selectedOrders } = this.state;
    const isSelected = selectedOrders.some(o => o.idorder === order.idorder);
    
    if (isSelected) {
      // Remover da seleção
      const newSelected = selectedOrders.filter(o => o.idorder !== order.idorder);
      this.calculateTotals(newSelected);
    } else {
      // Adicionar à seleção
      const newSelected = [...selectedOrders, {
        idorder: order.idorder,
        valor: order.pendente,
        texto: order.doc || `Order ${order.idorder}`,
      }];
      this.calculateTotals(newSelected);
    }
  }

  updateOrderValue(orderId, newValue) {
    const order = this.state.pendingOrders.find(o => o.idorder === orderId);
    if (!order) return;

    const maxValue = parseFloat(order.pendente);
    const value = Math.min(parseFloat(newValue) || 0, maxValue);

    const newSelected = this.state.selectedOrders.map(o => 
      o.idorder === orderId ? { ...o, valor: value } : o
    );
    
    this.calculateTotals(newSelected);
  }

  calculateTotals(selectedOrders) {
    // Calcular totais baseado nas orders selecionadas
    // A API vai calcular o IVA proporcional, então apenas somamos os valores
    const total_liquido = selectedOrders.reduce((sum, o) => sum + parseFloat(o.valor || 0), 0);
    
    // Aplicar desconto no recibo
    const desconto = parseFloat(this.state.valor_desc_recibo || 0);
    const total_com_desconto = Math.max(0, total_liquido - desconto);
    
    // Para uma estimativa, vamos assumir 23% de IVA (o backend calcula corretamente)
    const estimatedIva = total_com_desconto * 0.23 / 1.23;
    const total_iliquido = total_com_desconto - estimatedIva;
    
    this.setState({
      selectedOrders,
      total_valor_liquido: total_com_desconto,
      total_valor_iliquido: total_iliquido,
      total_valor_iva: estimatedIva,
    });
  }

  async handleFormSubmit(e) {
    const profile = this.context;
    const { history } = this.props;
    e.preventDefault();

    const { provider, selectedOrders, date, forma_pagamento, numero_cheque, banco, obs, data_pre_datado, valor_desc_recibo } = this.state;

    if (!provider) {
      NotificationManager.error("Selecione um fornecedor", "Erro", 3000);
      return;
    }

    if (selectedOrders.length === 0) {
      NotificationManager.error("Selecione pelo menos uma order para liquidar", "Erro", 3000);
      return;
    }

    this.setState({
      isSubmitting: true,
    });

    try {
      // Extrair data e hora do campo datetime-local
      let data = '';
      let date_time = 0;
      
      if (date) {
        // Formato datetime-local: YYYY-MM-DDTHH:mm
        const dateObj = new Date(date);
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        data = `${year}-${month}-${day}`;
        date_time = dateToTimestamp(date);
      }

      // data_pre_datado já vem no formato YYYY-MM-DD quando inputType é "date"
      const data_pre_datado_formatted = data_pre_datado || null;

      const payload = {
        idempresa: profile.idEmpresa,
        fornecedor: provider.idprovider,
        forma_pagamento: forma_pagamento,
        numero_cheque: numero_cheque || "",
        banco: banco || "",
        saldo: 0,
        valor_desc_recibo: parseFloat(valor_desc_recibo) || 0,
        obs: obs || "",
        data: data,
        date_time: date_time,
        data_pre_datado: data_pre_datado_formatted,
        orders: selectedOrders.map(o => ({
          idorder: o.idorder,
          valor: parseFloat(o.valor),
          texto: o.texto || "",
        })),
      };

      const response = await ordersService.setPayment(payload);

      if (response && response.success) {
        NotificationManager.success(
          `Recibo ${response.numero} criado com sucesso`,
          "Recibo de Fornecedor",
          3000
        );
        history.push("/payments");
      } else {
        NotificationManager.error(
          response?.error || "Erro ao criar recibo",
          "Erro",
          3000
        );
      }
    } catch (error) {
      console.error('Erro ao criar recibo:', error);
      const errorMessage = error?.response?.data?.error || "Erro ao criar recibo. Por favor, tente novamente.";
      NotificationManager.error(errorMessage, "Erro", 5000);
    } finally {
      this.setState({ isSubmitting: false });
    }
  }

  handleFormCancel() {
    const { history } = this.props;
    history.push("/payments");
  }

  render() {
    const { location, history } = this.props;
    const {
      loaded,
      providers,
      provider,
      pendingOrders,
      selectedOrders,
      date,
      forma_pagamento,
      numero_cheque,
      banco,
      obs,
      data_pre_datado,
      valor_desc_recibo,
      isSubmitting,
      total_valor_iliquido,
      total_valor_iva,
      total_valor_liquido,
    } = this.state;
    const cookies = new URLSearchParams(location.search);

    const paymentMethods = [
        {
            value: 1,
            label: "Dinheiro"
        },
        {
            value: 2,
            label: "Crédito"
        },
        {
            value: 3,
            label: "Cheque"
        },
        {
            value: 4,
            label: "TPA"
        },
        {
            value: 16,
            label: "Nota de Crédito"
        },
        {
            value: 104,
            label: "Transferência"
        },
    ];

    const canSubmit = provider && selectedOrders.length > 0 && !isSubmitting;

    return (
      <div>
        <Menu
          loadFunction={this.loadFunction}
          location={location}
          history={history}
          newCookies={cookies}
        />
        <div className="fullContainer" style={{ maxWidth: "100%" }}>
          <div className="col-xs-12">
            <ol
              style={{
                textAlign: "left",
                marginTop: "75px",
              }}
              className="breadcrumb"
            >
              <li>
                <a href="http://eposgestofinal.com/index.php">Portal</a>
              </li>
              <li>
                <a href="/payments">Pagamentos</a>
              </li>
              <li className="active">
                <span>Novo Recibo de Fornecedor</span>
              </li>
            </ol>
          </div>
        </div>
        {loaded && (
          <div className="fullContainer" style={{ maxWidth: "100%" }}>
            <div className="col-sm-12" style={{ textAlign: "center" }}>
              <div className="panel panel-default">
                <div className="container-fluid">
                  <div className="invoice-panel">
                    <div className="header">
                      <h1 className="invoice-title">Novo Recibo de Fornecedor</h1>
                    </div>
                    <form onSubmit={this.handleFormSubmit}>
                      {/* Cabeçalho */}
                      <div style={{ border: '1px solid #ccc', padding: '20px', borderRadius: '20px', marginBottom: '20px' }}>
                        <div className="form-grid" style={{ margin: 0 }}>
                          <div className="form-group">
                            <label className="form-label">Fornecedor</label>
                            <AutoCompleteField
                              options={providers}
                              value={provider}
                              onChange={async (_, selectedProvider) => {
                                this.setState({
                                  provider: selectedProvider,
                                  pendingOrders: [],
                                  selectedOrders: [],
                                });
                                if (selectedProvider) {
                                  await this.loadPendingOrders(selectedProvider.idprovider);
                                }
                              }}
                              getOptionLabel={(option) => option.name || ""}
                              label="Selecione um fornecedor"
                              required
                            />
                          </div>

                          <div className="form-group">
                            <label className="form-label">Método de Pagamento</label>
                            <AutoCompleteField
                              options={paymentMethods}
                              value={paymentMethods.find(
                                (pm) => pm.value === forma_pagamento
                              )}
                              onChange={(_, pm) =>
                                this.setState({ forma_pagamento: pm.value })
                              }
                              getOptionLabel={(option) => option.label || ""}
                              label="Selecione o método de pagamento"
                              required
                            />
                          </div>

                          <div className="form-group">
                            <label className="form-label">Data e Hora</label>
                            <Input
                              inputType="datetime-local"
                              name="date"
                              value={date}
                              handleChange={(e) =>
                                this.setState({ date: e.target.value })
                              }
                            />
                          </div>

                          <div className="form-group">
                            <label className="form-label">Desconto no Recibo</label>
                            <TextField
                              type="number"
                              min="0"
                              step="0.01"
                              value={valor_desc_recibo}
                              onChange={(e) => {
                                this.setState({
                                  valor_desc_recibo: e.target.value,
                                });
                                // Recalcular totais quando o desconto muda
                                setTimeout(() => {
                                  this.calculateTotals(this.state.selectedOrders);
                                }, 0);
                              }}
                            />
                          </div>
                        </div>

                        {/* Campos do Cheque */}
                        {forma_pagamento === 3 && (
                          <div className="form-grid" style={{ margin: '20px 0 0 0' }}>
                            <div className="form-group">
                              <label className="form-label">Número do Cheque</label>
                              <TextField
                                type="text"
                                value={numero_cheque}
                                onChange={(e) =>
                                  this.setState({ numero_cheque: e.target.value })
                                }
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Banco</label>
                              <TextField
                                type="text"
                                value={banco}
                                onChange={(e) =>
                                  this.setState({ banco: e.target.value })
                                }
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Data Pré-datado (opcional)</label>
                              <Input
                                inputType="date"
                                name="data_pre_datado"
                                value={data_pre_datado || ""}
                                handleChange={(e) =>
                                  this.setState({
                                    data_pre_datado: e.target.value || null,
                                  })
                                }
                              />
                            </div>
                          </div>
                        )}

                        <div className="form-group">
                          <label className="form-label">Observações</label>
                          <TextField
                            type="text"
                            label="Observações do documento (opcional)"
                            value={obs}
                            onChange={(e) =>
                              this.setState({ obs: e.target.value })
                            }
                          />
                        </div>
                      </div>

                  {/* Linhas */}
                  {provider && (
                    <div className="products-section">
                      <h2 className="products-title">Linhas</h2>
                      {pendingOrders.length === 0 ? (
                        <div
                          style={{
                            textAlign: "center",
                            padding: "40px",
                            color: "#64748b",
                          }}
                        >
                          Não existem orders pendentes para este fornecedor.
                        </div>
                      ) : (
                        <>
                          {pendingOrders.map((order) => {
                            const isSelected = selectedOrders.some(
                              (o) => o.idorder === order.idorder
                            );
                            const selectedOrder = selectedOrders.find(
                              (o) => o.idorder === order.idorder
                            );

                            return (
                              <div key={order.idorder} className="product-grid receipt-grid">
                                <div className="product-field">
                                  <TextField
                                    type="text"
                                    label="Documento"
                                    value={order.doc || `Order ${order.idorder}`}
                                    readOnly
                                  />
                                </div>

                                <div className="product-field">
                                  <TextField
                                    type="text"
                                    label="Data"
                                    value={new Date(order.date_time * 1000).toLocaleDateString('pt-PT')}
                                    readOnly
                                  />
                                </div>

                                <div className="product-field">
                                  <TextField
                                    type="text"
                                    label="Valor em Falta"
                                    value={parseFloat(order.pendente || 0).toFixed(2)}
                                    readOnly
                                  />
                                </div>

                                <div className="input-field">
                                  <span>Liquidar</span>
                                  <div className="switch-with-field">
                                    <div className="switch-wrapper">
                                      <Switch
                                        checked={isSelected}
                                        handleChange={() => this.toggleOrder(order)}
                                      />
                                    </div>
                                    {isSelected && (
                                      <div className="valor-liquidar-field">
                                        <TextField
                                          type="number"
                                          min="0.01"
                                          step="0.01"
                                          max={order.pendente}
                                          label="Valor a Liquidar"
                                          value={selectedOrder?.valor || order.pendente}
                                          onChange={(e) => {
                                            const maxValue = parseFloat(order.pendente);
                                            let newValue = parseFloat(e.target.value) || 0;
                                            if (newValue > maxValue) {
                                              newValue = maxValue;
                                            }
                                            this.updateOrderValue(order.idorder, newValue);
                                          }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>
                  )}

                  {/* Resumo Financeiro */}
                  {provider && selectedOrders.length > 0 && (
                    <div className="totals">
                    <div style={{ 
                        marginBottom: '2rem', 
                        fontSize: '1.5rem', 
                        fontWeight: '700', 
                        color: '#1e293b',
                        textAlign: 'center',
                        letterSpacing: '0.025em',
                        textTransform: 'uppercase'
                      }}>
                        Resumo Financeiro
                      </div>
                      {parseFloat(valor_desc_recibo || 0) > 0 && (
                        <div className="total-row">
                          <span className="total-label">Desconto</span>
                          <span className="total-value">-{parseFloat(valor_desc_recibo || 0).toFixed(2)} €</span>
                        </div>
                      )}
                      <div className="total-row total-final">
                        <span className="total-label">TOTAL</span>
                        <span className="total-value">{total_valor_liquido.toFixed(2)} €</span>
                      </div>
                    </div>
                  )}

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: "16px",
                          marginTop: "32px",
                          padding: "24px",
                          backgroundColor: "white",
                          borderRadius: "12px",
                          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => history.push("/payments")}
                          className="back-button"
                        >
                          <MdTurnLeft size={28} />&nbsp;&nbsp;Voltar
                        </button>
                        <button
                          type="submit"
                          disabled={!canSubmit}
                          className="submit-button"
                        >
                          {isSubmitting ? (
                            <ClipLoader size={20} />
                          ) : (
                            <MdAddCircleOutline size={28} />
                          )}
                          &nbsp;&nbsp;Criar Recibo
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default withCookies(ProvidersForm);

